import React, { useState, useRef, useEffect, useContext } from 'react';
import { useTranslation } from "react-i18next";
import { AuthContext } from '../../Header/auth - context';
import './Podrska.css';

const WELCOME = `Zdravo! Ja sam TMS Asistent — vaša AI podrška za svakodnevni rad u sistemu.

Mogu da vam pomognem sa:
• Korišćenjem modula (ture, magacin, servis, fakture...)
• Poslovnim pravilima (EES Schengen dani, dozvole, tahograf)
• Pregledom podataka iz sistema (vozila, zaposleni, ture...)

Kako mogu da pomognem?`;

const SUGGESTIONS = [
  'Koliko imamo vozila?',
  'Prikaži aktivne zaposlene',
  'Koje ture imamo ovog meseca?',
  'Prikaži stanje magacina',
  'Ima li otvorenih servisa?',
  'Kako da dodam novu turu?',
  'Objasni EES proveru',
  'Koje uloge postoje u sistemu?',
];

const fmt = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
};

const Podrska = () => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: WELCOME, time: new Date() },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    const userMsg = { role: 'user', content: msg, time: new Date() };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setLoading(true);

    try {
      const r = await fetch(process.env.REACT_APP_BACKEND_URL + '/support/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth.token,
        },
        body: JSON.stringify({
          messages: updated.map(m => ({ role: m.role, content: m.content })),
        }),
      });
      const data = await r.json();
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: r.ok ? data.reply : ('Greška: ' + (data.message || 'Pokušajte ponovo.')),
          time: new Date(),
        },
      ]);
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Greška pri komunikaciji sa serverom.', time: new Date() },
      ]);
    }
    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleSuggestion = (s) => {
    setDropdownOpen(false);
    send(s);
  };

  const showChips = messages.length === 1 && !loading;

  return (
    <div className="podrska-wrapper">

      {/* Header */}
      <div className="podrska-header">
        <div className="podrska-header-avatar">
          <i className="pi pi-android" />
        </div>
        <div>
          <div className="podrska-header-title">TMS AI Asistent</div>
        </div>

        {/* Suggestions dropdown — top right */}
        <div className="podrska-dropdown-wrap" ref={dropdownRef}>
          <button
            className="podrska-suggestions-btn"
            onClick={() => setDropdownOpen(o => !o)}
            title="Predložena pitanja"
          >
            <i className="pi pi-list" />
            Pitanja
            <i className={`pi pi-chevron-${dropdownOpen ? 'up' : 'down'} podrska-chevron`} />
          </button>

          {dropdownOpen && (
            <div className="podrska-dropdown">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} className="podrska-dropdown-item" onClick={() => handleSuggestion(s)}>
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="podrska-messages">
        {messages.map((m, i) => (
          <div key={i} className={`msg-row ${m.role}`}>
            {m.role === 'assistant' && (
              <div className="msg-avatar">AI</div>
            )}
            <div className="msg-body">
              <div className="msg-bubble">{m.content}</div>
              <div className="msg-time">{fmt(m.time)}</div>
            </div>
          </div>
        ))}

        {loading && (
          <div className="msg-row assistant">
            <div className="msg-avatar">AI</div>
            <div className="typing-bubble">
              <div className="typing-dot" />
              <div className="typing-dot" />
              <div className="typing-dot" />
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Quick suggestion chips (shown only at start) */}
      {showChips && (
        <div className="podrska-chips">
          {SUGGESTIONS.map((s, i) => (
            <button key={i} className="podrska-chip" onClick={() => send(s)}>
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="podrska-input-area">
        <textarea
          ref={textareaRef}
          className="podrska-textarea"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder={t('home.pitajteNesto')}
          rows={1}
          disabled={loading}
        />
        <button
          className="podrska-send-btn"
          onClick={() => send()}
          disabled={!input.trim() || loading}
          title="Pošalji"
        >
          <i className="pi pi-send" />
        </button>
      </div>

    </div>
  );
};

export default Podrska;
